import {ReactNode, useCallback, useEffect, useState} from 'react';
import {requestContext} from '../../../../../../common/requestContext';
import SmoothKeyScroller from '../../../../../../common/SmoothKeyScroller';
import {stopPropagationEvent} from '../../../../../../common/shared/utils/stopPropagationEvent.function';
import {twMerge} from '../../../../../../common/twMerge';
import {useAddWindowEventListener} from '../../../../../../common/useAddWindowEventListener.hook';
import useTypedDispatch from '../../../../../../common/useTypedDispatch';
import useTypedSelector from '../../../../../../common/useTypedSelector';
import {PC_VIEWER_HEADER_HEIGHT} from '../../../../../../design-system/organisms/PcHeader/constants';
import {MOBILE_HEADER_HEIGHT_PX} from '../../../../../../header/constants';
import {setIsViewerMenuExposed} from '../state/viewerMenuSlice';
import {ImageViewerReadType} from '../type/ImageViewerReadType';
import {ImageViewerSeekbarContainer} from './ImageViewerSeekbarContainer';
import {PageImageViewerContainer} from './PageImageViewerContainer';
import {ScrollImageViewerContainer} from './ScrollImageViewerContainer';

export function ImageViewerContainer({
  seriesId,
  productId,
  isPreview = false,
  children,
}: {
  seriesId: number;
  productId: number;
  isPreview?: boolean;
  children?: ReactNode;
}) {
  const isMobile = requestContext.requestInfo.display.isMobile;

  const readType = useTypedSelector(
    state => state.imageViewer.settings.readType
  );

  const isViewerMenuExposed = useTypedSelector(
    state => state.viewerMenu.isExposed
  );

  const dispatch = useTypedDispatch();

  const [imageViewerRootElement, setImageViewerRootElement] =
    useState<HTMLDivElement | null>(null);

  useEffect(() => {
    if (isPreview) {
      dispatch(setIsViewerMenuExposed({isExposed: false}));
    }
  }, [dispatch, isPreview]);

  const handler = useCallback(() => {
    dispatch(setIsViewerMenuExposed({isExposed: !isViewerMenuExposed}));
  }, [dispatch, isViewerMenuExposed]);

  useAddWindowEventListener({
    element: imageViewerRootElement,
    type: 'click',
    listener: isPreview ? () => {} : handler,
  });
  const isBlackSpaceColor = useTypedSelector(
    state => state.imageViewer.settings.spaceColor === 'Black'
  );

  return (
    <div
      ref={setImageViewerRootElement}
      className={twMerge(
        'relative w-full',
        isBlackSpaceColor
          ? 'bg-viewer-static-epub-black-bg'
          : 'bg-viewer-static-epub-white-bg'
      )}
    >
      {/* 첫 화 보기에서는 시크바를 노출하지 않습니다. */}
      {!isPreview && (
        <div
          className={twMerge(
            'fixed inset-x-0 z-150 pb-safe',
            // 특정 iOS 사파리 브라우저에서 틈새가 발생하는 이슈를 위한 속성입니다. 1px 큰 요소를 만들어 배경으로 씁니다
            isMobile &&
              'before:absolute before:top-0 before:left-0 before:z-[-1] before:h-26pxr before:w-full before:bg-theme-bg-base'
          )}
          style={
            isMobile
              ? {
                  display: !isViewerMenuExposed ? 'none' : undefined,
                  bottom: MOBILE_HEADER_HEIGHT_PX,
                }
              : {
                  pointerEvents: !isViewerMenuExposed ? 'none' : undefined,
                  top: isViewerMenuExposed ? PC_VIEWER_HEADER_HEIGHT : 0,
                }
          }
          onClick={stopPropagationEvent}
        >
          <ImageViewerSeekbarContainer productId={productId} />
        </div>
      )}
      {/* 첫 화 보기는 스크롤 모드만 지원합니다. */}
      {(readType === ImageViewerReadType.Scroll || isPreview) && (
        <SmoothKeyScroller>
          <ScrollImageViewerContainer
            seriesId={seriesId}
            productId={productId}
            isPreview={isPreview}
          >
            {children}
          </ScrollImageViewerContainer>
        </SmoothKeyScroller>
      )}
      {readType === ImageViewerReadType.Page && !isPreview && (
        <PageImageViewerContainer seriesId={seriesId} productId={productId}>
          {children}
        </PageImageViewerContainer>
      )}
    </div>
  );
}
